/**
 * CSVで名簿を取り込む画面（issue #89・docs/01 6-4）。
 *
 * 手順は3つです。**押す前に必ず見込みを出して、そこで止まります。**
 *
 * ```
 * 手順1/3 ファイルを選ぶ → 手順2/3 見込みの確認（登録◯件・更新◯件・…） → 手順3/3 結果
 * ```
 *
 * 決めごと（docs/01 6-4・docs/06 原則1・2・4）:
 *  - 手順2で「取り込む」を押すまで、名簿は1行も変わりません
 *  - エラー行は「何行目・どの項目・なぜ・どう直すか」まで出します
 *  - **エラー行が1件でもあれば、押す前に注意を出します**（ほかの行は取り込まれます）
 *  - 結果はあとから何度でも見返せます（「取り込みの記録」）
 *  - 文字の形（UTF-8／Shift_JIS）はサーバが見分けます。お手元での変換は要りません
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { useSession } from '../auth/session-context.js';
import type { RosterColumnsResponse, RosterImportResult } from '../api/types.js';
import { RosterPhoneChanges } from '../components/RosterPhoneChanges.js';
import { RosterPhoneMissing } from '../components/RosterPhoneMissing.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { RosterCreateTable } from '../components/RosterCreateTable.js';
import { RosterErrorTable } from '../components/RosterErrorTable.js';
import { RosterSummaryList } from '../components/RosterSummaryList.js';
import {
  buildRosterTemplateCsv,
  csvDownloadUrl,
  revokeDownloadUrl,
  ROSTER_TEMPLATE_FILENAME,
} from '../roster/template.js';
import { endsWithRequest, withReason } from '../format/sentence.js';
import { FilePicker } from '../components/ui/FilePicker.js';
import { displayColumnLabel } from '../roster/errors.js';
import { ROLE_REQUIREMENTS, useRoleWording } from '../auth/role-wording.js';
import {
  confirmMessage,
  phoneChangeConfirmText,
  resultMessage,
  SELF_PHONE_CHANGED_NOTICE,
} from '../roster/summary-text.js';

type Step = 'choose' | 'preview' | 'done';

export function RosterImportPage(): ReactNode {
  const api = useApi();
  const { profile, signOut } = useSession();
  // 案内に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();
  const [step, setStep] = useState<Step>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<RosterColumnsResponse | null>(null);
  const [preview, setPreview] = useState<RosterImportResult | null>(null);
  const [result, setResult] = useState<RosterImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<{ message: string; nextStep: string | undefined } | null>(
    null,
  );
  /** 「CSVの書き方」の表を読めなかったときの言葉（黙って消さない・issue #207 L-3） */
  const [columnsError, setColumnsError] = useState<{ message: string; nextStep: string } | null>(
    null,
  );
  /** 役職で断られた（取り込みそのものができない） */
  const [deniedByRole, setDeniedByRole] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<RosterColumnsResponse>('/admin/roster/columns')
      .then((loaded) => {
        if (cancelled) return;
        setColumns(loaded);
        setColumnsError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // 項目の説明が読めなくても、取り込みそのものは行える。
        // ただし**黙って表を消さない**。表とひな型のボタンが揃って消えると、
        // 何を書けばよいか分からないまま取り込みへ進めてしまう
        setColumns({ columns: [] });
        setDeniedByRole(caught instanceof ApiError && caught.status === 403);
        // 取り込む権限が無いときに「開き直すと出ます」と言わない（総点検 J-85）
        setColumnsError(
          caught instanceof ApiError && caught.status === 403
            ? {
                // 役職の呼び名はその会社のもの（issue #480）
                message: wording.only(
                  ROLE_REQUIREMENTS.rosterImport,
                  'CSVでまとめて登録できるのは',
                ),
                nextStep: wording.ask(ROLE_REQUIREMENTS.rosterImport),
              }
            : {
                message: withReason(
                  'CSVの項目の決まりを読み込めませんでした',
                  caught instanceof ApiError ? caught.message : messageForStatus(0),
                ),
                nextStep:
                  'この表とひな型は、画面を開き直すと出ます。前にお使いになったCSVと同じ見出しであれば、そのまま取り込んでいただけます。',
              },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [api, wording]);

  const templateUrl = useMemo(() => {
    if (!columns || columns.columns.length === 0) return null;
    return csvDownloadUrl(buildRosterTemplateCsv(columns.columns));
  }, [columns]);

  useEffect(() => {
    return () => {
      if (templateUrl) revokeDownloadUrl(templateUrl);
    };
  }, [templateUrl]);

  function fail(caught: unknown, headline: string): void {
    const reason = caught instanceof ApiError ? caught.message : messageForStatus(0);
    setError({
      message: withReason(headline, reason),
      // 理由の文が次の一手まで言っているとき（「もう一度『中身を確かめる』から始めてください。」
      // など）は、食い違う決まり文句を足さない（#367 U-5）
      nextStep: endsWithRequest(reason)
        ? undefined
        : 'ファイルの中身をご確認のうえ、もう一度お試しください。それでも直らないときは、担当者へお知らせください。',
    });
  }

  async function runPreview(): Promise<void> {
    if (!file) {
      setError({
        message: 'ファイルが選ばれていません。',
        nextStep: '「ファイルを選ぶ」から、取り込むCSVを選んでください。',
      });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const contentBase64 = await readAsBase64(file);
      const previewed = await api.post<RosterImportResult>('/admin/roster/imports/preview', {
        body: { contentBase64, sourceName: file.name },
      });
      setPreview(previewed);
      setStep('preview');
    } catch (caught) {
      fail(caught, '中身を確かめられませんでした。');
    } finally {
      setBusy(false);
    }
  }

  async function runImport(): Promise<void> {
    if (!file || !preview || preview.summary.created + preview.summary.updated === 0) return;
    setBusy(true);
    setError(null);
    try {
      const contentBase64 = await readAsBase64(file);
      const executed = await api.post<RosterImportResult>('/admin/roster/imports', {
        body: { contentBase64, sourceName: file.name, previewId: preview.id },
      });
      // ご自身の電話番号が変わったときは、このあとサーバがこの画面のログインも切る（#408）。
      // 次の操作で「ログインの有効期限が切れました」と出て理由が分からなくならないよう、
      // その場でログアウトして、なぜ・次に何をするかをログインの画面に出す（#475 W-15・決定22）
      if ((executed.phoneChanges ?? []).some((change) => change.isYou)) {
        signOut(SELF_PHONE_CHANGED_NOTICE);
        return;
      }
      setResult(executed);
      setStep('done');
    } catch (caught) {
      fail(caught, '取り込めませんでした。');
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  function startOver(): void {
    setStep('choose');
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  }

  const known = columns?.columns ?? [];
  // 取り込む権限の無い方には、断りだけを出す（ファイルを選ぶ欄・空の表を残さない・#329-11）
  const denied = columnsError !== null && columns?.columns.length === 0 && deniedByRole;
  // 「名簿を一括で取り込む」だけがあり「名簿を管理する」が無い役職では、どの行も同じ理由で
  // 断られる（docs/01 15-1）。行ごとのエラーで埋める前に、画面に1つの案内として出す（issue #184 L-10）
  const lacksMemberManage =
    profile !== null &&
    profile !== undefined &&
    profile.grant.capabilities['roster.manage'] === true &&
    profile.grant.capabilities['member.manage'] !== true;

  if (lacksMemberManage) {
    return (
      <article>
        <PageHeader
          title="CSVでまとめて登録"
          description="CSVから、メンバーの登録・更新をまとめて行います。"
        />
        <ResultBanner
          tone="info"
          message="いまの役職では、CSVでの登録はお使いいただけません。CSVで登録するには「メンバーをCSVでまとめて登録する」に加えて「メンバーを管理する（招待・無効にする）」も要ります。"
          nextStep={`「役職とできること」で両方を付けるか、${wording.who(ROLE_REQUIREMENTS.rosterImport)}へご依頼ください。`}
        />
      </article>
    );
  }

  if (denied) {
    return (
      <article>
        <PageHeader
          title="CSVでまとめて登録"
          description="CSVから、メンバーの登録・更新をまとめて行います。"
        />
        <ResultBanner tone="info" message={columnsError.message} nextStep={columnsError.nextStep} />
      </article>
    );
  }

  return (
    <article>
      <PageHeader
        title="CSVでまとめて登録"
        description="CSVから、メンバーの登録・更新をまとめて行います。押す前に、何件がどうなるかを必ずお見せします。"
        actions={
          <Link className="ui-button ui-button-secondary" to="/roster/imports">
            取り込みの記録
          </Link>
        }
      />

      <ol className="admin-steps" aria-label="取り込みの手順">
        <li aria-current={step === 'choose' ? 'step' : undefined}>手順 1／3 ファイルを選ぶ</li>
        <li aria-current={step === 'preview' ? 'step' : undefined}>手順 2／3 取り込む前の確認</li>
        <li aria-current={step === 'done' ? 'step' : undefined}>手順 3／3 結果</li>
      </ol>

      {error ? (
        <ResultBanner tone="error" message={error.message} nextStep={error.nextStep} />
      ) : null}

      {step === 'choose' ? (
        <>
          <FormCard
            title="手順 1／3 ファイルを選ぶ"
            description="Excel で保存したCSVも、そのまま使えます。"
            submitLabel="中身を確かめる"
            submitBusyLabel="確かめています…"
            busy={busy}
            onSubmit={() => void runPreview()}
          >
            <FilePicker
              id="roster-file"
              label="CSVファイル"
              accept=".csv,text/csv"
              file={file}
              onChange={setFile}
            />
            <p className="admin-note">
              この時点ではメンバーの登録内容は変わりません。次の画面で件数をご確認いただいてから取り込みます。
            </p>
          </FormCard>

          <Card
            title="CSVの書き方"
            description="1行目に下の見出しを並べ、2行目から1人ずつ書きます。並び順は自由です。"
            actions={
              templateUrl ? (
                <a
                  className="ui-button ui-button-secondary"
                  href={templateUrl}
                  download={ROSTER_TEMPLATE_FILENAME}
                >
                  ひな型（空のCSV）をダウンロード
                </a>
              ) : undefined
            }
          >
            {columnsError ? (
              <ResultBanner
                tone="info"
                message={columnsError.message}
                nextStep={columnsError.nextStep}
              />
            ) : null}
            <DataTable label="CSVの項目">
              <thead>
                <tr>
                  <th scope="col">見出し</th>
                  <th scope="col">必ず要るか</th>
                  <th scope="col">この書き方でも受け取れます</th>
                </tr>
              </thead>
              <tbody>
                {known.map((column) => (
                  <tr key={column.key}>
                    <th scope="row">{displayColumnLabel(column.label)}</th>
                    <td>
                      {column.required
                        ? '必ず要ります'
                        : column.key === 'phone'
                          ? 'ログインに使います（空の方はログインできません）'
                          : (column.note ?? '空でもかまいません')}
                    </td>
                    <td className="ui-cell-wrap">{column.aliases.join('／')}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            <p className="admin-note">
              役職の欄は、新しく登録する方の最初の役職としてだけ使います。すでにいる方の役職は、CSVでは変わりません（変更はメンバーの画面から1人ずつ行います）。
            </p>
          </Card>
        </>
      ) : null}

      {step === 'preview' && preview ? (
        <Card
          title="手順 2／3 取り込む前の確認"
          description="取り込むと、こうなります。まだメンバーの登録内容は変わっていません。新しく登録される方は、お名前まで下にお出しします。"
        >
          <RosterSummaryList summary={preview.summary} />

          {preview.summary.errors > 0 ? (
            <ResultBanner
              tone="error"
              message={`取り込めない行が${preview.summary.errors}件あります。この行は取り込まれません。`}
              nextStep={
                preview.summary.created + preview.summary.updated > 0
                  ? `残りの${preview.summary.created + preview.summary.updated}件はそのまま取り込まれます。先にCSVを直したい場合は「やめる」を押して、直してからもう一度お選びください。`
                  : '「やめる」を押してCSVの内容を直し、もう一度お選びください。'
              }
            />
          ) : null}

          {preview.summary.ignoredHeaders.length > 0 ? (
            <ResultBanner
              tone="info"
              message={`読み飛ばした見出しがあります：${preview.summary.ignoredHeaders.join('／')}`}
              nextStep="この列は取り込みません。必要な項目であれば、見出しの名前を上の表に合わせてください。"
            />
          ) : null}

          <RosterErrorTable issues={preview.errors} columns={known} />

          {/* どなたが新しく登録されるかを、押す前にお見せする（issue #203） */}
          <RosterCreateTable creates={preview.creates} />

          {/* 電話番号が空のままになる方（ログインは電話番号＋認証番号だけ・#341）。取り込みは止めない */}
          <RosterPhoneMissing rows={preview.phoneMissing ?? []} />

          {/* 電話番号が変わる方は、前のスマホのログインが切れる（#408・参加の申し込みの承認と同じ）。
              どなたの番号がどう変わるかを並べ、ご自身が含まれるときは赤い帯で知らせる（#444） */}
          {preview.phoneChanges !== undefined ? (
            <RosterPhoneChanges rows={preview.phoneChanges} />
          ) : phoneChanges(preview) > 0 ? (
            <ResultBanner
              tone="info"
              message={`電話番号が変わる方が${phoneChanges(preview)}名います。取り込むと、その方の前のスマホのログインは切れます。`}
              nextStep="新しい電話番号でログインし直していただくよう、ご本人にお伝えください。"
            />
          ) : null}

          {preview.deactivationCandidates.length > 0 ? (
            <section>
              <h3>CSVに載っていなかった方（{preview.deactivationCandidates.length}名）</h3>
              <p>
                この方々は<strong>自動では無効にしません</strong>
                。退職された方であれば、メンバーの画面でお一人ずつ無効にしてください。
              </p>
              <ul>
                {preview.deactivationCandidates.map((candidate) => (
                  <li key={candidate.userId}>
                    {candidate.displayName}
                    {candidate.sameNameAsNewEntry ? (
                      <>
                        {' '}
                        <strong>← 同じお名前の方が、上の「新しく登録される方」にもいます</strong>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {preview.summary.created + preview.summary.updated === 0 ? (
            <p role="status">
              {preview.summary.errors === 0 &&
              preview.summary.rows > 0 &&
              preview.summary.unchanged === preview.summary.rows
                ? '今の登録と同じ内容のため、取り込むものはありません。'
                : '取り込める行がありません。'}
            </p>
          ) : null}
          <div className="ui-form-actions">
            <Button
              variant="primary"
              busy={busy}
              busyLabel="取り込んでいます…"
              disabled={preview.summary.created + preview.summary.updated === 0}
              onClick={() => setConfirming(true)}
            >
              取り込む
            </Button>
            <Button variant="secondary" onClick={startOver} disabled={busy}>
              やめる（ファイルを選び直す）
            </Button>
          </div>
        </Card>
      ) : null}

      {step === 'done' && result ? (
        <Card title="手順 3／3 結果">
          <ResultBanner
            tone={result.summary.errors > 0 ? 'info' : 'success'}
            message={resultMessage(result.summary)}
            nextStep={
              result.summary.errors > 0
                ? '取り込めなかった行は下の表のとおりです。CSVを直して、もう一度取り込んでください。'
                : undefined
            }
          />
          <RosterSummaryList summary={result.summary} />
          <RosterErrorTable issues={result.errors} columns={known} />
          {(result.signedOutElsewhere ?? 0) > 0 ? (
            <p className="admin-note">
              電話番号が変わった{result.signedOutElsewhere}
              名は、前のスマホのログインを切りました。新しい電話番号でログインし直していただくよう、ご本人にお伝えください。
            </p>
          ) : null}

          <div className="ui-form-actions">
            <Button variant="primary" onClick={startOver}>
              もう一度取り込む
            </Button>
            <Link className="ui-button ui-button-secondary" to={`/roster/imports/${result.id}`}>
              この結果をあとから見る
            </Link>
          </div>
        </Card>
      ) : null}

      <ConfirmDialog
        open={confirming}
        title="CSVからメンバーを登録・更新します"
        message={
          preview
            ? confirmMessage(preview.summary.created, preview.summary.updated) +
              (preview.summary.errors > 0
                ? `取り込めない${preview.summary.errors}件は、そのまま残ります。`
                : '') +
              phoneChangeConfirmText(preview.phoneChanges ?? [])
            : ''
        }
        reversibility="取り込んだ内容は、この画面からは元に戻せません（直したCSVで取り込み直す形になります）。"
        tone="primary"
        confirmLabel="取り込む"
        busy={busy}
        onConfirm={() => void runImport()}
        onCancel={() => setConfirming(false)}
      />
    </article>
  );
}

/**
 * ファイルの中身を、**文字に直す前のまま**送れる形にする。
 *
 * 文字に直してから送らないのは、Excel から出した台帳（Shift_JIS）をそのまま
 * お出しいただけるようにするためです。どの文字の形かはサーバが見分けます（docs/01 6-4）。
 */
async function readAsBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('ファイルを読み取れませんでした'));
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(file);
  });
  // 「data:text/csv;base64,〜」の、コンマから後ろがファイルの中身
  const separator = dataUrl.indexOf(',');
  if (separator < 0) throw new Error('ファイルを読み取れませんでした');
  return dataUrl.slice(separator + 1);
}

/**
 * 電話番号が変わる方の人数（見込みの「更新される方」のうち、電話番号の項目が変わる方）。
 * 変わる方の一覧（phoneChanges）を返さない古いサーバのときだけ使う。
 */
function phoneChanges(preview: RosterImportResult): number {
  return preview.updates.filter((update) => update.fields.includes('電話番号')).length;
}
